import { faEnvelope, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import React, { useRef, useEffect } from "react";
import { Badge, Card } from "@mantine/core";
import { CommunicationMechanism, CommunicationType } from "../../../data/api/adminCommunicationApi";

interface CommunicationPreviewProps {
  communication: any | null;
  persons: { [id: string]: any };
}

const CommunicationPreview: React.FunctionComponent<CommunicationPreviewProps> = ({
  communication,
  persons,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (communication?.email?.bodyHtml && iframeRef.current) {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (iframeDoc) {
        // Inject dark mode aware CSS
        const css = `
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              margin: 0; 
              padding: 20px; 
              line-height: 1.5;
              color: var(--color-text-primary);
              background-color: var(--color-admin-card);
            }
            [data-bs-theme="dark"] body {
              color: var(--color-text-primary);
              background-color: var(--color-admin-card);
            }
            a { color: var(--color-button-primary); }
            [data-bs-theme="dark"] a { color: var(--color-button-primary); }
            img { max-width: 100%; height: auto; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid var(--color-table-border); }
            [data-bs-theme="dark"] th, [data-bs-theme="dark"] td { border-color: var(--color-table-border); }
          </style>
        `;
        
        const html = css + communication.email.bodyHtml;
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();

        // Apply dark mode theme to iframe if needed
        const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
        if (isDark) {
          iframeDoc.documentElement.setAttribute('data-bs-theme', 'dark');
        }
      }
    }
  }, [communication]);

  const getPersonName = (personId: string) => {
    const person = persons[personId];
    return person ? person.preferredName || person.fullName : 'Unknown';
  };

  const getRecipients = (to: string | string[]) => {
    if (Array.isArray(to)) {
      return to.map(id => getPersonName(id)).join(', ');
    } else {
      return getPersonName(to);
    }
  };

  const getSender = (sender: string | null) => {
    if (!sender || typeof sender === 'string') {
      return sender || 'System';
    }
    return getPersonName(sender);
  };

  const formatDate = (date: string | Date) => {
    return DateTime.fromISO(date.toString()).toFormat('MMM d, yyyy \'at\' h:mm a');
  };

  const getMechanismColor = (mechanism: CommunicationMechanism) => {
    switch (mechanism) {
      case CommunicationMechanism.email:
        return "primary";
      case CommunicationMechanism.sms:
        return "success";
      case CommunicationMechanism.notification:
        return "warning";
      default:
        return "secondary";
    }
  };

  const getMechanismText = (mechanism: CommunicationMechanism) => {
    switch (mechanism) {
      case CommunicationMechanism.email:
        return "Email";
      case CommunicationMechanism.sms:
        return "SMS";
      case CommunicationMechanism.notification:
        return "Push Notification";
      default:
        return "Unknown";
    }
  };

  const getCommunicationTypeColor = (communicationType: string) => {
    const type = communicationType as CommunicationType;
    switch (type) {
      case CommunicationType.Rostered:
        return "info";
      case CommunicationType.Unrostered:
        return "warning";
      case CommunicationType.BankDetailsChanged:
        return "secondary";
      case CommunicationType.PaymentNotification:
        return "success";
      default:
        return "secondary";
    }
  };

  const getCommunicationTypeText = (communicationType: string) => {
    const type = communicationType as CommunicationType;
    switch (type) {
      case CommunicationType.Rostered:
        return "Rostered";
      case CommunicationType.Unrostered:
        return "Unrostered";
      case CommunicationType.BankDetailsChanged:
        return "Bank Details Changed";
      case CommunicationType.PaymentNotification:
        return "Payment Notification";
      default:
        return communicationType;
    }
  };

  if (!communication) {
    return (
      <div className="h-100 d-flex flex-column">
        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
          <div className="text-center">
            <FontAwesomeIcon icon={faEnvelope} size="3x" className="text-muted mb-3" />
            <h5 className="text-muted">Select a communication to view</h5>
            <p className="text-muted">Choose a communication from the list to see its details</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-100 d-flex flex-column">
      <div className="d-flex flex-column h-100">
        <div className="p-3 border-bottom flex-shrink-0">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="mb-2">
                {communication.email?.subject || 'No Subject'}
              </h5>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Badge color={getMechanismColor(communication.type)}>
                  {getMechanismText(communication.type)}
                </Badge>
                {communication.communicationType && (
                  <Badge color={getCommunicationTypeColor(communication.communicationType)}>
                    {getCommunicationTypeText(communication.communicationType)}
                  </Badge>
                )}
                {communication.dateSent ? (
                  <Badge color="green" variant="outline">
                    Sent {formatDate(communication.dateSent)}
                  </Badge>
                ) : (
                  <Badge color="yellow" variant="outline">
                    Scheduled {formatDate(communication.date)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-2">
            <div className="mb-1">
              <strong>From:</strong> {getSender(communication.sender)}
            </div>
            <div className="mb-1">
              <strong>To:</strong> {getRecipients(communication.to)}
            </div>
            <div className="mb-1">
              <strong>Created:</strong> {formatDate(communication.date)}
            </div>
            {communication.email?.from && (
              <div className="mb-1">
                <strong>Reply-To:</strong> {communication.email.from}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex-grow-1 position-relative">
          {communication.email?.bodyHtml ? (
            <iframe
              ref={iframeRef}
              className="w-100 h-100 border-0 position-absolute"
              style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              title="Email Content"
              sandbox="allow-same-origin"
            />
          ) : (
            <div className="p-4 h-100 d-flex align-items-center justify-content-center">
              <div className="text-center">
                <FontAwesomeIcon icon={faFileAlt} size="2x" className="text-muted mb-3" />
                <p className="text-muted">No content available</p>
              </div>
            </div>
          )}
        </div>
        
        {communication.email?.attachments && communication.email.attachments.length > 0 && (
          <div className="border-top p-3 flex-shrink-0">
            <h6>Attachments ({communication.email.attachments.length})</h6>
            {communication.email.attachments.map((attachment, index) => (
              <div key={index} className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={faFileAlt} className="me-2 text-muted" />
                <span>{attachment.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationPreview;
